<script setup lang="ts">
import {IonButton, IonIcon} from '@ionic/vue'
import {computed, PropType, ref, Ref, watch} from 'vue'
import {DataLoader} from "@/helper/dataLoader.js";
import {Jobsite, ShiftType} from "@/graphql/generated/graphql";
import Skeleton from "@/components/Skeleton.vue";
import Modal from "@/components/Modal.vue"
import MonthCalendarGrid from "@/components/MonthCalendarGrid.vue";
import {kapitelDateString, kapitelMonthString} from "@/graphql/kapitelTypes";
import month from "@/helper/datetime/month";
import {toMap} from "@/helper/array";
import ButtonSegment from "@/components/ButtonSegment.vue";
import {chevronBack, chevronForward, toggle} from "ionicons/icons";
import {sortShifts} from "@/helper/shiftTypes";
import JobsiteDemandDayDetail from "@/views/JobsiteDemandDayDetail.vue";
import {fetchDemandForMonth, fetchPastDemandForMonth} from "@/helper/demandBrowser";
import {useGlobalEmitter} from "@/helper/emitter";
import {defaultMonth, defaultShiftSelection, useDemandBrowserFilterSelectionStore} from "@/store/demandBrowser";
import date from "@/helper/datetime/date";
import JobsiteDetails from "@/views/JobsiteDetails.vue";
import {KapitelIcon} from "@/helper/icon";
import MonthCalendarLegend from "@/components/MonthCalendarLegend.vue";
import ButtonToggle from "@/components/ButtonToggle.vue";
import {isSet} from "@/helper/isset";
import {watchDebounced} from "@vueuse/core";

// external model: jobsite
const jobsite = defineModel({type: Object as PropType<Jobsite | undefined>, required: false})
/* external props:
    month: preselected month - defaults to todays month
    shiftTypes: preselected shifts - defaults to shiftPortfolio
 */
const props = defineProps({
    shiftSelection: {
        type: Array as PropType<ShiftType[]>,
        required: false,
    },

    startMonth: {
        type: String as PropType<kapitelMonthString>,
        required: false,
        default: undefined
    },

    showJobsiteDetailModalLink: {
        type: Boolean,
        required: false,
        default: true
    }
})

const demandBrowserFilterSelectionStore = useDemandBrowserFilterSelectionStore()

// internal state: isOpen depending on jobsite - bidirectional
const isOpen = ref()
watch(()=>jobsite.value, (value) => isOpen.value = !!value, {immediate: true}) // track external jobsite setting
watch(()=>isOpen.value, (value) =>{ if(!value) { jobsite.value = undefined } } ) // feedback to outside if modal is closed from within

// internal state: selected shifts
const shiftsSelected = ref<ShiftType[]>(defaultShiftSelection())
watch(() => props.shiftSelection, (v) => {if (isSet(v)) {shiftsSelected.value = v}}); // track external shift selection
const setShiftSelection = (selection: ShiftType[]) => {
    shiftsSelected.value = selection;
    demandBrowserFilterSelectionStore.setShifts(shiftsSelected.value)
} // store manual shift selection changes in store

// internal state: current month
const monthSelected = ref<kapitelMonthString>(defaultMonth())
watch(() => props.startMonth, (v) => {if (isSet(v)) {monthSelected.value = v}}); // track external start month
const setMonthSelected = (month: kapitelMonthString) => {
    monthSelected.value = month;
    demandBrowserFilterSelectionStore.setMonth(monthSelected.value)
} // store manual shift selection changes in store

const initSelections = async () => {
    await demandBrowserFilterSelectionStore.ensureInit()

    if (isSet(props.shiftSelection)) {
        shiftsSelected.value = props.shiftSelection;
    } else {

    }

    if (isSet(props.startMonth)) {
        monthSelected.value = props.startMonth;
    } else {

    }


}

// data loader
const dataLoader = new DataLoader();

// demand data
const demand : Ref<DemandJoDaSh[]> = ref([])
const pastDemand : Ref<DemandJoDaSh[]> = ref([])
const demandByDay = computed(() => toMap(demand.value, d => d.date))
const pastDemandByDay = computed(() => toMap(pastDemand.value, d => d.date))
dataLoader.add(
    async (signal) => demand.value = jobsite.value ? await fetchDemandForMonth(jobsite.value.id, monthSelected.value, shiftsSelected.value, {signal}) : [],
)
dataLoader.add(
    async (signal) => pastDemand.value = jobsite.value ? await fetchPastDemandForMonth(jobsite.value.id, monthSelected.value, shiftsSelected.value, {signal}) : []
)

const showJobsiteDetailsModalModel: Ref<number|undefined> = ref(undefined)
const showJobsiteDetailsModal = () => showJobsiteDetailsModalModel.value = jobsite.value?.id

/**
 * events
 */
const dayDetailOpen = ref(true)
const selectedDate = ref()
const onCalendarDateClick = ({date}: { date: kapitelDateString }) => {selectedDate.value = date; dayDetailOpen.value = true}
const onClickSegment = (newSelection) => {
    const index = shiftsSelected.value.indexOf(newSelection.key)
    if (index > -1) {
        setShiftSelection(shiftsSelected.value.toSpliced(index, 1))
    }
    else {
        setShiftSelection(shiftsSelected.value.concat([newSelection.key]))
    }

}

const init = async () => {
    await initSelections();
    await dataLoader.reload()
}


watchDebounced(() => {return monthSelected.value + shiftsSelected.value.join(',')}, () => {dataLoader.reload()}, {debounce: 50})


useGlobalEmitter().on('day:mutated', dataLoader.reload)


/**
 * rendersets
 */
const availableShifts = Object.entries(ShiftType).toSorted(([key1, label1], [key2, label2]) => sortShifts(label1, label2)).map(([key, label]) => ({key:label, label}))

</script>

<template>
    <Modal
        v-model="isOpen"
        @view:willPresent="init"
        :transparentToolbar="true"
    >
        <h2 class="globalHeaderWithSubHeader">Offene Dienste</h2>
        <h4 class="globalHeaderWithSubHeader">
            auf {{jobsite?.abbreviation}}
            <a v-if="showJobsiteDetailModalLink" @click="showJobsiteDetailsModal">
                <IonIcon :icon="KapitelIcon.infoFill"  class="globalTextAlignedIcon"/>
            </a>
        </h4>

        <JobsiteDetails v-if="showJobsiteDetailModalLink" v-model="showJobsiteDetailsModalModel" :showJobsiteDemandModalLink="false" />

        <p style="display:flex; align-items: center; justify-content: center;">
            <IonButton @click="setMonthSelected(month.sub(monthSelected, 1))" color="white"><IonIcon :icon="chevronBack"/> </IonButton>
            <b style="flex-grow: 99; font-size: var(--custom-font-size-larger); margin: 1em; text-align: center">{{ month.formatKapitelMonthYear(monthSelected) }}</b>
            <IonButton @click="setMonthSelected(month.add(monthSelected, 1))" color="white"> <IonIcon :icon="chevronForward" /> </IonButton>
        </p>

        <div class="rich-month-calendar">
            <Skeleton v-if="dataLoader.pending.value" :items="['block--height-18rem']"></Skeleton>
            <MonthCalendarGrid
                v-else
                :date="monthSelected"
                v-on:date-click="onCalendarDateClick"
            >
                <template #date="{ date, adjacent }" >
                    <div
                        v-if="demandByDay.has(date.id)"
                        :class="'calendar-date-content demand ' + (demandByDay.get(date.id)?.inesIsConsidering ? 'is-considering' : '')"
                    />
                    <div
                        v-else-if="pastDemandByDay.has(date.id)"
                        class="calendar-date-content demand past-demand"
                    />
                </template>
            </MonthCalendarGrid>

            <MonthCalendarLegend mode="demand"/>
        </div>
        <div class="button-segment">
            <ButtonToggle
                v-for="segment in availableShifts"
                :key="segment.key"
                :modelValue="shiftsSelected.indexOf(segment.key)>=0"
                @click="onClickSegment(segment)"
            >
                <div class="button-toggle__inner">
                    <span>{{ segment.label || segment.key }}</span>
                    <IonIcon :class="shiftsSelected.indexOf(segment.key)>=0  ? 'active' : 'inactive'" :icon="toggle"/>
                </div>
            </ButtonToggle>
        </div>

        <JobsiteDemandDayDetail v-if="jobsite && selectedDate" :jobsite="jobsite" :date="selectedDate" v-model="dayDetailOpen" />
    </Modal>
</template>

<style scoped lang="scss">
.calendar-date-content {
    $SIZE: 0.5em;
    width: $SIZE;
    height: $SIZE;
    border-radius: 50%;
    transform: translateY(1.1em);

    &.demand {
        background-color: var(--ion-color-red);
        &.is-considering {
            background-color: var(--ion-color-green);
        }
        &.past-demand {
            background-color: var(--ion-color-red-tint-2);
        }
    }
}

.button-segment {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left:1rem;
    padding-right:1rem;
    >.button-toggle {

        .button-toggle__inner {
            padding: 0.5rem 1rem;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
        }
        &:not(.active) {
            --background: rgba(var(--ion-color-light-rgb), 0.7);
            --background-hover: rgba(var(--ion-color-light-rgb), 0.7);
            --background-activated: rgba(var(--ion-color-light-rgb), 0.7);
            --background-focused: rgba(var(--ion-color-light-rgb), 0.7);
            ion-icon {
                transform: rotate(180deg);
            }
        }
        ion-icon  {
            font-size: 17px;
        }
    }
    border-radius: 1em;

    ion-button {
        margin-top: 6px;
        margin-bottom: 6px;
        & + ion-button {
            margin-top: 6px;
            margin-bottom: 6px;
        }
    }
}
</style>
